import { useMemo } from 'react';
import type { Comment } from '../../containers/CommentList/CommentList.types';

/**
 * Custom hook to find all comments where the logged-in user is mentioned
 *
 * Uses timestamp-based approach (Approach 2):
 * - Compares mention timestamps with readByMentionTimestamp
 * - Any mention after the last read timestamp is considered unread
 * - Filters comments based on:
 *   - User is mentioned in the commentMentions field (either in main comment or replies)
 *   - Comment/reply is not resolved
 *   - Mention timestamp is after the last read timestamp
 *
 * @param comments - Array of comments to search through
 * @param loggedInUserEmail - Email of the logged-in user to check for mentions
 * @returns Array of comment IDs that have unread mentions
 */
export const useMostRecentMention = (
  comments: Comment[],
  loggedInUserEmail?: string
): string[] => {
  return useMemo(() => {
    if (!loggedInUserEmail || comments.length === 0) return [];

    const highlightedCommentIds: string[] = [];

    // Helper to check if user is mentioned and mention is after last read time (DRY principle)
    const hasUnreadUserMention = (
      comment: Comment,
      parentLastReadTime?: number
    ): boolean => {
      const hasMention = comment.commentMentions?.some(
        mention => mention.email === loggedInUserEmail
      );

      if (!hasMention) return false;

      const commentTime = new Date(
        comment.editedAt || comment.createdAt
      ).getTime();

      // For replies, use parent comment's lastRead timestamp
      // For main comments, use their own lastRead timestamp
      const lastReadTime =
        parentLastReadTime !== undefined
          ? parentLastReadTime
          : comment.readByMentionTimestamp
          ? new Date(comment.readByMentionTimestamp).getTime()
          : 0;

      return commentTime > lastReadTime;
    };

    // Helper to add comment ID if it has unread mentions (DRY principle)
    const addHighlightedComment = (id: string): void => {
      if (!highlightedCommentIds.includes(id)) {
        highlightedCommentIds.push(id);
      }
    };

    // Iterate through comments
    for (const comment of comments) {
      // Early return if comment is closed - no need to check mentions
      if (comment.commentStatus === 'RESOLVED') continue;

      // Check main comment
      if (comment.createdAt && hasUnreadUserMention(comment)) {
        addHighlightedComment(comment.id);
      }

      // Check replies - use parent comment's lastRead timestamp
      if (comment.replies) {
        const parentLastReadTime = comment.readByMentionTimestamp
          ? new Date(comment.readByMentionTimestamp).getTime()
          : 0;

        for (const reply of comment.replies) {
          if (
            reply.createdAt &&
            reply.commentStatus !== 'RESOLVED' &&
            hasUnreadUserMention(reply, parentLastReadTime)
          ) {
            // Add parent comment ID for highlighting the entire thread
            addHighlightedComment(comment.id);
          }
        }
      }
    }

    return highlightedCommentIds;
  }, [comments, loggedInUserEmail]);
};
